// Cliente HTTP — fetch wrapper com tratamento de erro padrão.
export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: any) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...init.headers,
    },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) {
    throw new ApiError(res.status, typeof body === "object" ? body?.error ?? res.statusText : res.statusText, body);
  }
  return body as T;
}

export const api = {
  get:  <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : null }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : null }),
  del:  (path: string) => request<void>(path, { method: "DELETE" }),
};

// Tipos de payload do backend
export interface UserMe {
  user: { id: number; email: string; name: string | null; superAdmin: boolean };
  memberships: Array<{ slug: string; name: string; role: "admin" | "member" | "viewer"; theme?: Theme }>;
}

export interface Theme {
  logoUrl: string | null;
  primary: string;
  accent: string;
}

export interface TenantInfo {
  tenant: {
    slug: string;
    name: string;
    status: string;
    theme: Theme;
    timezone: string;
    currency: string;
  };
  role: "admin" | "member" | "viewer";
  capabilities: Record<string, boolean>;
}

export interface KpiCard {
  current: number;
  prev: number;
  target: number | null;
  change_pct: number | null;
}
export interface StrategicKpis {
  monthly_revenue: KpiCard;
  orders: KpiCard;
  organic_traffic: KpiCard;
  avg_order_value: KpiCard;
  roas: KpiCard;
  conversion_rate: KpiCard;
}

export interface TrendPoint {
  date: string;
  revenue: number;
  orders: number;
  organic_sessions: number;
}

export interface TaskRow {
  id: number;
  title: string;
  description: string | null;
  phase: string | null;
  front: string | null;
  owner: string | null;
  due_date: string | null;
  status: "pending" | "in_progress" | "done" | "blocked" | "cancelled";
  completed_at: string | null;
}

export interface IntegrationRow {
  id: number;
  type: string;
  label: string | null;
  status: "active" | "paused" | "error";
  last_run_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
  has_credentials: boolean;
}

export interface AlertRow {
  id: number;
  severity: "info" | "warning" | "critical";
  kind: string;
  title: string;
  detail: string | null;
  source: string | null;
  created_at: string;
  acknowledged_at: string | null;
}

export type Role = "admin" | "member" | "viewer";

export interface InvitePublic {
  email: string;
  role: Role;
  tenant_slug: string;
  tenant_name: string;
}

export interface InviteRow {
  id: number;
  email: string;
  role: Role;
  tenant_slug: string;
  tenant_name: string;
  invited_by_email: string | null;
  status: "pending" | "accepted" | "expired";
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
}

export interface AdminUserRow {
  id: number;
  email: string;
  name: string | null;
  is_super_admin: boolean;
  email_verified: boolean;
  created_at: string;
  memberships: Array<{ slug: string; role: Role }> | null;
}

export interface AdminTenantRow {
  id: number;
  slug: string;
  name: string;
}

export interface CreatedInvite {
  id: number;
  email: string;
  role: Role;
  tenant_slug: string;
  link: string;
  expires_at: string;
}
